<template>
  <ConnectionSegment
    :title="$t('connection-manager.connections.social-media.title')"
    :description="$t('connection-manager.connections.social-media.description')"
  >
    <SocialMediaWrapper
      v-for="connection in socialConnections"
      :key="connection.id"
      :provider="connection.provider"
      :connected-media="socialMediaConnections"
    >
      <template
        #activator="{ props, isConnected, loggedAs, loading, disabled, connect, disconnect }"
      >
        <Connection
          :active="isConnected"
          :disabled="disabled"
          :image="connection.image"
          :loading="loading"
          :dialogActivatorProps="props"
          :tooltip-content="disabled ? connection.tooltip : null"
          :needs-upgrade="connection.needsUpgrade"
          verified-text="connection-manager.connected"
          @connect="connect"
          @disconnect="confirmDisconnect(disconnect)"
        >
          <template #default>
            <div class="social-media-logged-as">
              {{ loggedAs }}
            </div>
          </template>
        </Connection>
      </template>
    </SocialMediaWrapper>
    <ConfirmationDialog
      ref="confirmationDialog"
      :title="$t('connection-manager.confirmation-dialog.title')"
      :text="$t('connection-manager.confirmation-dialog.social-text')"
      :ok-button-text="$t('connection-manager.confirmation-dialog.confirm-button-text')"
      color="error"
      ok-button-variant="text"
    />
  </ConnectionSegment>
</template>

<script lang="ts" setup>
import { computed, inject, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { AuthInstance } from '@/auth'
import ConfirmationDialog from '@/components/ConfirmationDialog.vue'
import { useSocialMedia } from '@/composables/useSocialMedia'
import { SocialMediaType } from '@/generated/graphql'
import { isLightUser } from '@/lib/productTypes'

import Connection from './Connection.vue'
import ConnectionSegment from './ConnectionSegment.vue'
import SocialMediaWrapper from './socialMediaWrapper/index.vue'

const { t } = useI18n()
const $auth = inject<AuthInstance>('auth')
const { socialMediaConnections, loadSocialMediaConnections, refetchSocialMediaConnections } =
  useSocialMedia()

const confirmationDialog = ref<InstanceType<typeof ConfirmationDialog> | null>(null)

const socialConnections = computed(() => [
  {
    id: 'facebook',
    image: '/img/facebook.png',
    provider: SocialMediaType.Facebook,
  },
  {
    id: 'instagram',
    image: '/img/instagram.png',
    provider: SocialMediaType.Instagram,
    tooltip: t('social-media.connect-instagram.info'),
  },
  {
    id: 'linkedin',
    image: '/img/linkedin.png',
    provider: SocialMediaType.Linkedin,
    needsUpgrade: isLightUser($auth?.user),
  },
])

const confirmDisconnect = async (disconnect: () => Promise<void>) => {
  if (!(await confirmationDialog.value?.confirm())) return
  await disconnect()
  await nextTick()
  await refetchSocialMediaConnections()
}

onMounted(() => {
  void loadSocialMediaConnections()
})
</script>

<style scoped>
.social-media-logged-as {
  padding: 10px 0;
}
</style>
